import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { cn } from "@/lib/utils"; // Shadcn's utility for conditional classes
import {
  BookOpen,
  LifeBuoy,
  Mail,
  PencilRuler,
  TrendingUp,
} from "lucide-react";
import { useState } from "react";

// ==================================================================
// ⚙️ CONFIGURATION AREA ⚙️
// This is the only place you need to edit for each app.
// ==================================================================
const APP_DATA = {
  appName: "Part-66 Exam Prep",
  appDescription: "Your EASA Part-66 Study Companion & Mock Exam App for iOS.",
  contactEmail: "support@tabbythecat.com",
  feedbackEmail: "feedback@tabbythecat.com",

  // --- FREQUENTLY ASKED QUESTIONS ---
  faq: [
    {
      question: "How do I select my default license category (e.g., B1.1, B2)?",
      answer:
        "When you first launch the app, you'll be asked to select your primary category. You can change this at any time by tapping the 'gear' icon on the main screen to go to Settings, then selecting a new category from the picker.",
    },
    {
      question: "What's the difference between Exam Mode and Study Mode?",
      answer:
        "Exam Mode simulates the real EASA exam with a strict time limit and no assistance. Your results are recorded to track your performance. Study Mode has no timer, and after you answer a question, the correct answer and an explanation (if available) are immediately shown, helping you learn.",
    },
    {
      question: "How do I restore my Pro subscription purchase?",
      answer:
        "If you've reinstalled the app or changed devices, go to the Settings screen (gear icon) and tap 'Restore Purchases'. This will sync with the App Store and unlock your Pro features.",
    },
    {
      question: "Why are most of the modules locked?",
      answer:
        "The app provides Module 1 and Module 2 for free so you can experience its features. Access to all 17 modules, unlimited exams, and detailed performance analytics requires a 'Part-66 Pro' subscription.",
    },
    {
      question: "How can I track my performance in a specific module?",
      answer:
        "For Pro users, you can press and hold on any module in the main list and select 'View Details'. This screen shows your average scores, allows you to log official exam results, and provides a detailed breakdown of your performance by subtopic and level.",
    },
  ],

  // --- USER GUIDES ---
  guides: [
    {
      title: "Getting Started",
      description: "First-time setup and interface overview.",
      icon: <LifeBuoy className="h-5 w-5 mr-2" />,
      content: [
        { type: "heading", text: "Welcome to Part-66 Exam Prep!" },
        {
          type: "paragraph",
          text: "This guide will walk you through setting up the app and understanding the main screen.",
        },
        { type: "heading", text: "1. Initial Category Selection" },
        {
          type: "paragraph",
          text: "When you first open the app, you'll see a welcome screen asking for your primary license category (e.g., B1.1). Your selection sets the default for generating exams. Don't worry, you can easily change this later in the app's settings.",
        },
        { type: "heading", text: "2. The Main Module List" },
        {
          type: "paragraph",
          text: "The main screen displays all 17 EASA Part-66 modules. Each module card shows its status:",
        },
        {
          type: "list",
          items: [
            "Closed Book Icon: The module is not yet marked as passed or failed.",
            "Green Checkmark Icon: You have manually marked this module as 'Passed'.",
            "Red X Icon: You have manually marked this module as 'Failed'.",
            "Lock Icon: This module requires a Pro subscription to access.",
          ],
        },
        {
          type: "paragraph",
          text: "Simply tap a module to begin.",
        },
      ],
    },
    {
      title: "Exam & Study Modes",
      description: "Learn the difference and how to use them.",
      icon: <PencilRuler className="h-5 w-5 mr-2" />,
      content: [
        { type: "heading", text: "Choosing the Right Mode" },
        {
          type: "paragraph",
          text: "After tapping a module, you will be prompted to choose between 'Exam Mode' and 'Study Mode'.",
        },
        { type: "heading", text: "Exam Mode: Test Your Knowledge" },
        {
          type: "paragraph",
          text: "This mode is designed to simulate the official EASA exam environment. Key features include:",
        },
        {
          type: "list",
          items: [
            "Timed Session: A countdown timer based on the official time allowed for that module/category.",
            "No Immediate Feedback: Answers are not revealed until you complete the entire exam.",
            "Performance Tracking: Your score, time taken, and performance by subtopic are saved and analyzed in your stats.",
          ],
        },
        { type: "heading", text: "Study Mode: Learn and Review" },
        {
          type: "paragraph",
          text: "This mode is a relaxed, pressure-free environment for learning the material.",
        },
        {
          type: "list",
          items: [
            "No Timer: Take as much time as you need to answer each question.",
            "Instant Feedback: As soon as you select an answer, you'll see if it was correct. The correct answer and a detailed explanation will be displayed.",
            "No Score Tracking: Study sessions are not recorded in your overall performance statistics.",
          ],
        },
      ],
    },
    {
      title: "Tracking Your Performance",
      description: "Understand your stats and log results.",
      icon: <TrendingUp className="h-5 w-5 mr-2" />,
      content: [
        { type: "heading", text: "Your Performance Dashboard" },
        {
          type: "paragraph",
          text: "For Pro users, the dashboard at the top of the main screen gives you a quick overview of your progress, including your overall average score, study streak, and weakest module.",
        },
        { type: "heading", text: "Detailed Module View" },
        {
          type: "paragraph",
          text: "To dive deeper, press and hold on any module and select 'View Details'. Here you can:",
        },
        {
          type: "list",
          items: [
            "Log Official Results: Manually enter your official exam score and date. If you mark it as failed, the app will remind you of the 90-day retake rule.",
            "Analyze In-App Performance: See detailed charts of your performance by subtopic and level based on the mock exams you've taken in the app.",
            "Review Exam History: See a list of your recent mock exam scores for that specific module.",
            "Add Notes: Keep personal notes or reminders for each module.",
          ],
        },
      ],
    },
  ],
};
// ==================================================================
// SUB-COMPONENT FOR THE GUIDES TAB
// This component handles the two-column layout for the guides.
// ==================================================================
function GuidesContent() {
  // Set the first guide as the default selected one
  const [selectedGuide, setSelectedGuide] = useState(APP_DATA.guides[0]);

  return (
    <Card>
      <div className="grid grid-cols-1 md:grid-cols-12 min-h-[600px]">
        {/* Left Column: List of Guides */}
        <div className="md:col-span-4 border-b md:border-b-0 md:border-r p-4">
          <h3 className="text-lg font-semibold mb-4 px-2">All Guides</h3>
          <div className="flex flex-col space-y-1">
            {APP_DATA.guides.map((guide, index) => (
              <Button
                key={index}
                variant="ghost"
                className={cn(
                  "w-full flex justify-start items-center text-left h-auto py-2 cursor-pointer",
                  selectedGuide.title === guide.title &&
                    "bg-accent text-accent-foreground"
                )}
                onClick={() => setSelectedGuide(guide)}
              >
                {guide.icon}
                <div>
                  <p className="font-semibold text-wrap">{guide.title}</p>
                  <p className="text-xs text-muted-foreground text-wrap">
                    {guide.description}
                  </p>
                </div>
              </Button>
            ))}
          </div>
        </div>

        {/* Right Column: Guide Content */}
        <div className="md:col-span-8 p-6 sm:p-8">
          {selectedGuide && (
            <article className="prose prose-sm sm:prose-base max-w-none">
              {/* Render content based on its type */}
              {selectedGuide.content.map((block, index) => {
                switch (block.type) {
                  case "heading":
                    return (
                      <h2
                        key={index}
                        className="font-bold text-2xl mt-8 mb-4 border-b pb-2"
                      >
                        {block.text}
                      </h2>
                    );
                  case "paragraph":
                    return (
                      <p
                        key={index}
                        className="text-muted-foreground leading-7"
                      >
                        {block.text}
                      </p>
                    );
                  case "image":
                    return (
                      <img
                        key={index}
                        src={block.src}
                        alt={block.alt}
                        className="rounded-lg border bg-muted shadow-md w-full my-6"
                        loading="lazy"
                      />
                    );
                  case "list":
                    return (
                      <ul key={index} className="list-disc pl-6 space-y-2 my-4">
                        {block.items.map((item, i) => (
                          <li
                            key={i}
                            className="text-muted-foreground"
                            dangerouslySetInnerHTML={{
                              __html: item.replace(
                                /\*\*(.*?)\*\*/g,
                                "<strong>$1</strong>"
                              ),
                            }}
                          ></li>
                        ))}
                      </ul>
                    );
                  default:
                    return null;
                }
              })}
            </article>
          )}
        </div>
      </div>
    </Card>
  );
}

// ==================================================================
// MAIN SUPPORT PAGE COMPONENT
// ==================================================================
export default function SupportPage() {
  return (
    <div className="text-foreground mx-auto max-w-5xl px-6 pt-20">
      <div className="container mx-auto px-4 py-10 sm:py-16">
        <div className="text-center mb-12">
          <h1 className="text-4xl sm:text-5xl font-bold tracking-tight">
            {APP_DATA.appName} Support Center
          </h1>
          <p className="mt-3 text-lg text-muted-foreground max-w-2xl mx-auto">
            {APP_DATA.appDescription}
          </p>
        </div>

        <Tabs defaultValue="faq" className="w-full">
          <div className="flex flex-col sm:flex-row justify-between items-center mb-6 gap-4">
            <TabsList>
              <TabsTrigger value="faq" className="cursor-pointer">
                <LifeBuoy className="w-4 h-4 mr-2" /> FAQ
              </TabsTrigger>
              <TabsTrigger value="guides" className="cursor-pointer">
                <BookOpen className="w-4 h-4 mr-2" /> Guides
              </TabsTrigger>
              <TabsTrigger value="contact" className="cursor-pointer">
                <Mail className="w-4 h-4 mr-2" /> Contact Us
              </TabsTrigger>
            </TabsList>
            <div className="relative w-full sm:w-64">
              {/*<Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input placeholder="Search documentation..." className="pl-10" />*/}
            </div>
          </div>

          {/* FAQ Content */}
          <TabsContent value="faq">
            {/* ... (This part is unchanged) ... */}
            <Card>
              <CardHeader>
                <CardTitle>Frequently Asked Questions</CardTitle>
                <CardDescription>
                  Find quick answers to the most common questions.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Accordion type="single" collapsible className="w-full">
                  {APP_DATA.faq.map((item, index) => (
                    <AccordionItem value={`item-${index}`} key={index}>
                      <AccordionTrigger className="text-left font-semibold">
                        {item.question}
                      </AccordionTrigger>
                      <AccordionContent className="space-y-4 text-base">
                        <p className="text-muted-foreground">{item.answer}</p>
                        {item.screenshot && (
                          <img
                            src={item.screenshot}
                            alt={`Screenshot for ${item.question}`}
                            className="rounded-lg border bg-muted shadow-md w-full max-w-xl mx-auto"
                            loading="lazy"
                          />
                        )}
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Guides Content (Now uses the new component) */}
          <TabsContent value="guides">
            <GuidesContent />
          </TabsContent>

          {/* Contact Content */}
          <TabsContent value="contact">
            {/* ... (This part is unchanged) ... */}
            <Card>
              <CardHeader>
                <CardTitle>Get in Touch</CardTitle>
                <CardDescription>
                  Couldn't find an answer? We're here to help.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="flex items-start space-x-4 p-4 border rounded-lg">
                  <Mail className="h-6 w-6 text-primary flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-semibold">Email Support</h3>
                    <p className="text-muted-foreground">
                      This is the fastest way to get help. We typically respond
                      within 24 hours.
                    </p>
                    <Button asChild className="mt-3">
                      <a
                        href={`mailto:${APP_DATA.contactEmail}?subject=Support Request for ${APP_DATA.appName}`}
                      >
                        Contact Support
                      </a>
                    </Button>
                  </div>
                </div>
                <div className="flex items-start space-x-4 p-4 border rounded-lg">
                  <LifeBuoy className="h-6 w-6 text-primary flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-semibold">Feedback & Suggestions</h3>
                    <p className="text-muted-foreground">
                      Have an idea for a new feature? We'd love to hear it.
                    </p>
                    <Button variant="outline" asChild className="mt-3">
                      <a
                        href={`mailto:${APP_DATA.feedbackEmail}?subject=Feedback for ${APP_DATA.appName}`}
                      >
                        Share Feedback
                      </a>
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
